// Radar: signals, interactions, messages, inbox home.
import { apiCall, buildIdentityHeaders } from "./core";

export const radarAPI = {
  getSignals: async ({
    mode = null,
    venueId = null,
    token = null,
    userId = null,
  } = {}) => {
    const params = new URLSearchParams();
    if (mode) params.append("mode", mode);
    if (venueId) params.append("venue_id", venueId);
    const qs = params.toString();
    return await apiCall(`/radar/signals${qs ? `?${qs}` : ""}`, {
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
    });
    // Response includes: { city, signals[], rate_limit: { remaining, cooldownExpiresAt } }
  },

  upsertSignal: async ({
    mode = "social",
    intent = null,
    venueId = null,
    statusText = null,
    token = null,
    userId = null,
  } = {}) => {
    return await apiCall("/radar/signals", {
      method: "POST",
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
      body: JSON.stringify({
        mode,
        ...(intent ? { intent } : {}),
        ...(venueId ? { venue_id: venueId } : {}),
        ...(statusText ? { status_text: statusText } : {}),
      }),
    });
  },

  clearSignal: async ({ signalId, token = null, userId = null }) => {
    if (!signalId) throw new Error("signalId is required");
    return await apiCall(`/radar/signals/${signalId}`, {
      method: "DELETE",
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  getInbox: async ({ token = null, userId = null } = {}) => {
    return await apiCall("/signals/inbox", {
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  reportSignal: async ({ signalId, reason = null, token = null, userId = null } = {}) => {
    if (!signalId) throw new Error("signalId is required");
    return await apiCall(`/radar/signals/${signalId}/report`, {
      method: "POST",
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
      body: JSON.stringify(reason ? { reason } : {}),
    });
  },

  blockSignal: async ({ signalId, token = null, userId = null } = {}) => {
    if (!signalId) throw new Error("signalId is required");
    return await apiCall(`/radar/signals/${signalId}/block`, {
      method: "POST",
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  pingSignal: async ({
    signalId,
    note = null,
    token = null,
    userId = null,
  } = {}) => {
    if (!signalId) throw new Error("signalId is required");
    return await apiCall(`/signals/${signalId}/ping`, {
      method: "POST",
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
      body: JSON.stringify(note ? { note } : {}),
    });
  },

  acceptInteraction: async ({
    interactionId,
    token = null,
    userId = null,
  } = {}) => {
    if (!interactionId) throw new Error("interactionId is required");
    return await apiCall(`/signals/interactions/${interactionId}/accept`, {
      method: "POST",
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  requestReveal: async ({
    interactionId,
    token = null,
    userId = null,
  } = {}) => {
    if (!interactionId) throw new Error("interactionId is required");
    return await apiCall(
      `/signals/interactions/${interactionId}/request-reveal`,
      {
        method: "POST",
        credentials: "include",
        headers: buildIdentityHeaders({ token, userId }),
      },
    );
  },

  dismissInteraction: async ({
    interactionId,
    token = null,
    userId = null,
  } = {}) => {
    if (!interactionId) throw new Error("interactionId is required");
    return await apiCall(`/signals/interactions/${interactionId}/dismiss`, {
      method: "POST",
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  getInteractionMessages: async ({
    interactionId,
    token = null,
    userId = null,
  } = {}) => {
    if (!interactionId) throw new Error("interactionId is required");
    return await apiCall(`/signals/interactions/${interactionId}/messages`, {
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  sendInteractionMessage: async ({
    interactionId,
    body,
    token = null,
    userId = null,
  } = {}) => {
    if (!interactionId) throw new Error("interactionId is required");
    if (body == null || String(body).trim() === "")
      throw new Error("body is required");
    return await apiCall(`/signals/interactions/${interactionId}/messages`, {
      method: "POST",
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
      body: JSON.stringify({ body: String(body) }),
    });
  },
};

export const inboxHomeAPI = {
  getHome: async ({ token = null, userId = null } = {}) =>
    await apiCall("/inbox/home", {
      credentials: "include",
      headers: buildIdentityHeaders({ token, userId }),
    }),
};
